import type { AdCampaignCookie } from './utils';
import type { RouteLocationNormalized } from 'vue-router';

import { useBroadcastChannel, useSessionStorage } from '@vueuse/core';
import { BROADCAST_CHANNELS } from '~/constants/broadcastChannels';
import { deepToRaw } from '~/utils/deepToRaw';

import { getCampaignParamsFromRoute, routeHasCampaignParams } from './utils';

/**
 * Return type for the useAdCampaign composable
 */
interface UseAdCampaignReturn {
	/** Campaign data stored in sessionStorage and synced across tabs */
	adCampaign: Ref<AdCampaignCookie>;
	/** Whether there are active campaign parameters in the current session */
	hasCampaignInSession: ComputedRef<boolean>;
	/** Check if a route contains any recognized campaign parameters */
	routeHasCampaignParams: (route: RouteLocationNormalized) => boolean;
	/** Extract and normalize campaign parameters from a route's query */
	getCampaignParamsFromRoute: (
		route: RouteLocationNormalized
	) => AdCampaignCookie;
	/** Update campaign parameters and broadcast changes to other tabs */
	setAdCampaign: (campaignParams: AdCampaignCookie) => void;
	/** Get current campaign parameters */
	getAdCampaign: () => AdCampaignCookie;
}

/**
 * Message structure for cross-tab communication
 */
interface BroadcastMessage {
	/** Message type for synchronization between tabs */
	type: 'SYNC_REQUEST' | 'UPDATE';
	/** Campaign data being shared */
	data: AdCampaignCookie;
}

/**
 * Composable for managing advertising campaign parameters with cross-tab synchronization.
 *
 * Features:
 * - Session persistence using sessionStorage
 * - Cross-tab synchronization using BroadcastChannel
 * - Automatic data sync when new tabs open
 * - UTM parameter normalization
 *
 * @example
 * ```ts
 * const { adCampaign, setAdCampaign, getCampaignParamsFromRoute } = useAdCampaign();
 *
 * // Handle route with campaign parameters
 * const route = useRoute();
 * if (routeHasCampaignParams(route)) {
 *   const params = getCampaignParamsFromRoute(route);
 *   setAdCampaign(params);
 * }
 *
 * // Access campaign data
 * const campaignId = computed(() => adCampaign.value.adCampaignId);
 * ```
 *
 * @returns Methods and reactive data for campaign management
 */
export function useAdCampaign(): UseAdCampaignReturn {
	// Lazy initialization to avoid calling VueUse composables outside component context
	let _adCampaign: Ref<AdCampaignCookie> | null = null;
	let _broadcastChannel: ReturnType<
		typeof useBroadcastChannel<BroadcastMessage, BroadcastMessage>
	> | null = null;

	function initializeReactiveFeatures() {
		if (_adCampaign && _broadcastChannel) return;

		// Initialize reactive storage
		_adCampaign = useSessionStorage<AdCampaignCookie>('ad_campaign', {});

		// Initialize cross-tab communication
		_broadcastChannel = useBroadcastChannel<BroadcastMessage, BroadcastMessage>(
			{
				name: BROADCAST_CHANNELS.AD_CAMPAIGN_SYNC
			}
		);

		// Setup cross-tab message synchronization
		watch(
			_broadcastChannel.data,
			newData => {
				if (!newData?.data || !_adCampaign) return;

				const messageHandlers = {
					SYNC_REQUEST: () => {
						if (
							Object.keys(_adCampaign!.value).length > 0 &&
							_broadcastChannel
						) {
							_broadcastChannel.post({
								type: 'UPDATE',
								// deepToRaw: nested reactive values survive a shallow toRaw and
								// cannot be structured-cloned by BroadcastChannel → DataCloneError
								data: deepToRaw(_adCampaign!.value) as AdCampaignCookie
							});
						}
					},
					UPDATE: () => {
						if (_adCampaign) {
							_adCampaign.value = { ...newData.data };
						}
					}
				};

				messageHandlers[newData.type]?.();
			},
			{ immediate: true }
		);

		// Request data sync when a new tab opens
		watch(
			_broadcastChannel.isSupported,
			isReallySupported => {
				if (isReallySupported && _broadcastChannel) {
					_broadcastChannel.post({
						type: 'SYNC_REQUEST',
						data: {}
					});
				}
			},
			{ immediate: true }
		);
	}

	// Getters that initialize reactive features when accessed
	const adCampaign = computed({
		get: () => {
			initializeReactiveFeatures();
			return _adCampaign!.value;
		},
		set: value => {
			initializeReactiveFeatures();
			_adCampaign!.value = value;
		}
	});

	const hasCampaignInSession = computed<boolean>(
		() => Object.keys(adCampaign.value).length > 0
	);

	// Use the utility functions from utils.ts for these operations

	/**
	 * Update campaign parameters and broadcast changes to other tabs
	 * @param campaignParams - New campaign parameters to store
	 */
	function setAdCampaign(campaignParams: AdCampaignCookie): void {
		initializeReactiveFeatures();
		adCampaign.value = campaignParams;

		if (_broadcastChannel?.isSupported.value) {
			_broadcastChannel.post({
				type: 'UPDATE',
				data: deepToRaw(campaignParams) as AdCampaignCookie
			});
		}
	}

	/**
	 * Get current campaign parameters
	 * @returns Current campaign parameters from sessionStorage
	 */
	function getAdCampaign(): AdCampaignCookie {
		return unref(adCampaign.value);
	}

	return {
		adCampaign,
		hasCampaignInSession,
		routeHasCampaignParams, // Using imported function from utils
		getCampaignParamsFromRoute, // Using imported function from utils
		setAdCampaign,
		getAdCampaign
	};
}
